import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Avatar, Badge, Box, Button, Chip, CircularProgress,
  Container, Divider, Fade, Grid, IconButton, MenuItem,
  Paper, Snackbar, Stack, TextField, Tooltip, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ImageIcon from '@mui/icons-material/Image';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import PhoneIcon from '@mui/icons-material/Phone';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { useNavigate, useSearchParams } from 'react-router-dom';
import eventApi from '../EventGallery/eventApi';
import invitationApi from './invitationApi';
import { compressEventImage } from '../../utils/compressEventImage';
import networkApi from '../Network/services/networkApi';
import InvitationChat from './InvitationChat';
import MarkdownContent from '../EventGallery/EventMarkdown';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';

const RSVP_ICON = {
  accepted: <CheckCircleOutlineIcon fontSize="small" color="success" />,
  declined: <CancelOutlinedIcon fontSize="small" color="error" />,
  pending: <HourglassEmptyIcon fontSize="small" color="warning" />,
};
const RSVP_LABEL = { accepted: 'Attending', declined: 'Declined', pending: 'Pending' };

const COUNTRY_CODES = [
  { code: '+1', label: '+1 US' }, { code: '+44', label: '+44 UK' },
  { code: '+91', label: '+91 IN' }, { code: '+61', label: '+61 AU' },
  { code: '+971', label: '+971 UAE' }, { code: '+65', label: '+65 SG' },
  { code: '+60', label: '+60 MY' }, { code: '+27', label: '+27 ZA' },
  { code: '+33', label: '+33 FR' }, { code: '+49', label: '+49 DE' },
];

function avatarLetters(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

// ---------------------------------------------------------------------------
// Inline "add custom contact" row
// ---------------------------------------------------------------------------
function AddContactRow({ onSave, onCancel }) {
  const [name, setName] = useState('');
  const [channel, setChannel] = useState('email');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [cc, setCc] = useState('+44');
  const [err, setErr] = useState('');

  const save = () => {
    if (!name.trim()) { setErr('Name is required'); return; }
    if (channel === 'email' && !email.trim()) { setErr('Email is required'); return; }
    if (channel === 'phone' && !phone.trim()) { setErr('Phone is required'); return; }
    if (channel === 'whatsapp' && !phone.trim()) { setErr('WhatsApp number is required'); return; }
    setErr('');
    onSave({ name: name.trim(), email: email.trim(), phone: phone.trim(), country_code: cc, channel });
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'background.default' }}>
      <Stack spacing={1}>
        {err && <Alert severity="error" sx={{ py: 0 }}>{err}</Alert>}
        <TextField
          size="small" label="Full Name *" value={name}
          onChange={e => setName(e.target.value)} fullWidth
        />
        {/* Channel toggle */}
        <Stack direction="row" spacing={0.5}>
          {[
            { key: 'email', label: 'Email' },
            { key: 'phone', label: 'Phone', icon: <PhoneIcon fontSize="small" />, disabled: true },
            { key: 'whatsapp', label: 'WhatsApp', icon: <WhatsAppIcon fontSize="small" />, disabled: true },
          ].map(opt => (
            <Tooltip
              key={opt.key}
              title={opt.disabled ? 'Coming soon' : ''}
              disableHoverListener={!opt.disabled}
            >
              <span>
                <Button
                  size="small"
                  variant={channel === opt.key ? 'contained' : 'outlined'}
                  disabled={opt.disabled}
                  startIcon={opt.icon}
                  onClick={() => setChannel(opt.key)}
                  sx={{ textTransform: 'none', fontSize: 12 }}
                >
                  {opt.label}
                </Button>
              </span>
            </Tooltip>
          ))}
        </Stack>
        {channel === 'email' && (
          <TextField size="small" label="Email *" type="email" value={email}
            onChange={e => setEmail(e.target.value)} fullWidth />
        )}
        {(channel === 'phone' || channel === 'whatsapp') && (
          <Stack direction="row" spacing={1}>
            <TextField select size="small" value={cc} onChange={e => setCc(e.target.value)}
              sx={{ width: 110 }}>
              {COUNTRY_CODES.map(c => (
                <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>
              ))}
            </TextField>
            <TextField size="small" label="Number *" value={phone}
              onChange={e => setPhone(e.target.value.replace(/\D/g, ''))} fullWidth />
          </Stack>
        )}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={onCancel} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button size="small" variant="contained" onClick={save} sx={{ textTransform: 'none' }}>
            Add to list
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Single invitee row in the invite list
// ---------------------------------------------------------------------------
function InviteeRow({ invitee, savedInv, onRemove, onRevoke, onNudge, onChat }) {
  const name = invitee.customer_name || invitee.name || invitee.recipient_name || invitee.email || 'Contact';
  const email = invitee.email || invitee.recipient_email || '';
  const avatar = invitee.profile_picture || null;
  const status = savedInv?.rsvp_status || 'pending';
  const unread = savedInv?.unread_count || 0;
  const inviteCount = savedInv?.invite_count || 0;
  const nudgeDisabledToday = savedInv?.last_invited_at
    ? new Date(savedInv.last_invited_at).toDateString() === new Date().toDateString()
    : false;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, px: 1.5, py: 1, bgcolor: 'background.paper' }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Avatar src={avatar || undefined} sx={{ width: 36, height: 36, fontSize: 14 }}>
          {!avatar && avatarLetters(name)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>{name}</Typography>
          {email && <Typography variant="caption" color="text.secondary" display="block" noWrap>{email}</Typography>}
        </Box>
        {/* Status icon */}
        <Tooltip title={RSVP_LABEL[status] || status}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {RSVP_ICON[status] || RSVP_ICON.pending}
          </Box>
        </Tooltip>
        {/* Chat */}
        {savedInv && (
          <Tooltip title="Messages">
            <IconButton size="small" onClick={() => onChat(savedInv)}>
              <Badge badgeContent={unread || 0} color="error" max={9}>
                <ChatBubbleOutlineIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        )}
        {/* Nudge */}
        {savedInv && (
          <Tooltip title={nudgeDisabledToday ? 'Already nudged today' : `Nudge (${inviteCount}x sent)`}>
            <span>
              <IconButton
                size="small"
                disabled={nudgeDisabledToday}
                onClick={() => onNudge(savedInv.invitation_id)}
              >
                <SendIcon fontSize="small" color={nudgeDisabledToday ? 'disabled' : 'action'} />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Tooltip title={savedInv ? 'Revoke invitation' : 'Remove'}>
          <IconButton size="small" color={savedInv ? 'error' : 'default'}
            onClick={() => savedInv ? onRevoke(savedInv.invitation_id) : onRemove(invitee)}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function CreateInvitation() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user } = useAuth();

  const [searchParams] = useSearchParams();

  // The campaign being managed (from URL param or set after first send)
  const urlCampaignId = searchParams.get('campaign_id') || '';
  const [activeCampaignId, setActiveCampaignId] = useState(urlCampaignId);

  // Mobile step: 0 = draft, 1 = invites
  const [mobileStep, setMobileStep] = useState(0);

  // Left pane state
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [message, setMessage] = useState('');
  const [markdownPreview, setMarkdownPreview] = useState(false);
  const [coverFile, setCoverFile] = useState(null);        // new file selected locally
  const [coverPreview, setCoverPreview] = useState('');    // blob URL (local) or S3 URL (loaded)
  const [coverIsBlob, setCoverIsBlob] = useState(false);   // true = blob URL we must revoke
  const coverInputRef = useRef(null);

  // Track which campaign we've already restored draft state for
  const restoredForEventRef = useRef(null);

  // Stats
  const [stats, setStats] = useState({ total: 0, pending: 0, accepted: 0, declined: 0 });

  // Right pane: network search
  const [networkSearch, setNetworkSearch] = useState('');
  const [networkResults, setNetworkResults] = useState([]);
  const [networkLoading, setNetworkLoading] = useState(false);
  const searchTimerRef = useRef(null);

  // Right pane: invitee list (pending, not yet saved)
  const [invitees, setInvitees] = useState([]);

  // Right pane: saved invitations from backend
  const [savedInvitations, setSavedInvitations] = useState([]);

  // Right pane: add custom contact form
  const [showAddForm, setShowAddForm] = useState(false);

  // Chat popup
  const [chatInv, setChatInv] = useState(null);

  // Saving state
  const [saving, setSaving] = useState(false);

  // Loading state for initial campaign fetch
  const [pageLoading, setPageLoading] = useState(!!urlCampaignId);

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'info' });
  const toast = (msg, sev = 'info') => setSnack({ open: true, msg, sev });

  // Load user's events on mount
  useEffect(() => {
    eventApi.listMyEvents().then(data => {
      const created = data.created || [];
      setEvents(created);
      if (!activeCampaignId && created.length > 0) {
        setSelectedEventId(String(created[0].event_id));
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load saved recipients for a campaign; restore draft state on first load
  const loadSaved = useCallback(async (campaignId) => {
    if (!campaignId) return;
    const isFirstLoad = restoredForEventRef.current !== campaignId;
    try {
      const data = await invitationApi.listInvitations(campaignId);
      setSavedInvitations(data.invitations || []);
      setStats(data.stats || { total: 0, pending: 0, accepted: 0, declined: 0 });

      if (isFirstLoad) {
        restoredForEventRef.current = campaignId;
        if (data.campaign_name) setCampaignName(data.campaign_name);
        const first = data.invitations?.[0];
        const draftMessage = data.draft_message || first?.invitation_message;
        if (draftMessage) setMessage(draftMessage);
        const draftCoverUrl = data.draft_cover_url || first?.cover_image_url;
        if (draftCoverUrl) {
          setCoverPreview(draftCoverUrl);
          setCoverIsBlob(false);
          setCoverFile(null);
        }
        // Lock the event selector to this campaign's event
        if (first?.event_id) setSelectedEventId(String(first.event_id));
      }
    } catch {
      setSavedInvitations([]);
    } finally {
      if (isFirstLoad) setPageLoading(false);
    }
  }, []);

  // On mount: load campaign if campaign_id in URL
  useEffect(() => {
    if (activeCampaignId) loadSaved(activeCampaignId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset draft state when user manually changes event (fresh create only)
  useEffect(() => {
    if (activeCampaignId) return; // locked — don't reset
    setMessage('');
    setMarkdownPreview(false);
    setCoverFile(null);
    if (coverIsBlob && coverPreview) URL.revokeObjectURL(coverPreview);
    setCoverPreview('');
    setCoverIsBlob(false);
    setSavedInvitations([]);
    setStats({ total: 0, pending: 0, accepted: 0, declined: 0 });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventId]);

  // Cleanup blob URL on unmount only
  useEffect(() => () => { if (coverIsBlob && coverPreview) URL.revokeObjectURL(coverPreview); }, []);

  // Network search with debounce
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (networkSearch.trim().length < 2) { setNetworkResults([]); return; }
    searchTimerRef.current = setTimeout(async () => {
      setNetworkLoading(true);
      try {
        const data = await networkApi.searchConnections(networkSearch.trim());
        setNetworkResults(Array.isArray(data) ? data : []);
      } catch {
        setNetworkResults([]);
      } finally {
        setNetworkLoading(false);
      }
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [networkSearch]);

  const selectedEvent = events.find(e => String(e.event_id) === String(selectedEventId));
  // Lock event selector once a campaign exists
  const isEventLocked = !!activeCampaignId;

  const saveButtonLabel = invitees.length > 0
    ? `Save & Send (${invitees.length} new)`
    : (activeCampaignId || savedInvitations.length > 0)
      ? 'Save Changes'
      : 'Save Draft';

  // Combine pending invitees + saved into master list for display
  const allInviteesById = useMemo(() => {
    const map = {};
    for (const inv of savedInvitations) {
      const key = inv.recipient_id
        ? `cm_${inv.recipient_id}`
        : `email_${inv.recipient_email}`;
      map[key] = inv;
    }
    return map;
  }, [savedInvitations]);

  const isAlreadyAdded = (person) => {
    if (person.customer_id) return Boolean(allInviteesById[`cm_${person.customer_id}`]);
    if (person.email) return Boolean(allInviteesById[`email_${person.email}`]);
    return false;
  };
  const isInQueue = (person) =>
    invitees.some(i =>
      (person.customer_id && i.customer_id === person.customer_id) ||
      (person.email && i.email === person.email)
    );

  function addToQueue(person) {
    if (isAlreadyAdded(person) || isInQueue(person)) return;
    setInvitees(prev => [...prev, person]);
    setNetworkSearch('');
    setNetworkResults([]);
  }

  function removeFromQueue(person) {
    setInvitees(prev => prev.filter(i =>
      !(person.customer_id && i.customer_id === person.customer_id) &&
      !(person.email && i.email === person.email)
    ));
  }

  function handleCoverChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast('Please select an image file.', 'error'); return; }
    if (coverIsBlob && coverPreview) URL.revokeObjectURL(coverPreview);
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
    setCoverIsBlob(true);
  }

  async function handleSendInvitations() {
    if (!selectedEventId) { toast('Please select an event.', 'error'); return; }

    setSaving(true);
    try {
      let coverKey = null;

      // Upload new cover image if a file was just selected (compressed to 2048px)
      if (coverFile) {
        const tempInvId = `tmp_${Date.now()}`;
        const urlData = await invitationApi.getCoverUploadUrl(Number(selectedEventId), tempInvId);
        const compressed = await compressEventImage(coverFile);
        await fetch(urlData.upload_url, {
          method: 'PUT',
          headers: { 'Content-Type': 'image/jpeg' },
          body: compressed,
        });
        coverKey = urlData.key;
      }

      const draftPayload = {
        campaign_name: campaignName.trim() || null,
        invitation_message: message.trim() || null,
        ...(coverKey ? { cover_image_key: coverKey } : {}),
      };

      // Update existing campaign's message/cover/name (covers both already-sent
      // recipients and a campaign that's still just a draft placeholder)
      if (activeCampaignId) {
        await invitationApi.saveCampaignDraft(activeCampaignId, draftPayload);
      }

      let refreshCampaignId = activeCampaignId;

      // Send to new invitees
      if (invitees.length > 0) {
        const inviteePayload = invitees.map(inv => ({
          customer_id: inv.customer_id || null,
          email: inv.email || inv.recipient_email || null,
          name: inv.customer_name || inv.name || null,
          phone: inv.phone || null,
          country_code: inv.country_code || null,
        }));
        const res = await invitationApi.createInvitations({
          event_id: Number(selectedEventId),
          campaign_id: activeCampaignId || undefined,
          invitees: inviteePayload,
          ...draftPayload,
        });
        setInvitees([]);
        refreshCampaignId = res.campaign_id || activeCampaignId;

        // Store campaign_id from first send and update URL so subsequent saves work
        if (res.campaign_id && !activeCampaignId) {
          setActiveCampaignId(res.campaign_id);
          navigate(`/invitations/create?campaign_id=${res.campaign_id}`, { replace: true });
        }
        toast(`${inviteePayload.length} invitation${inviteePayload.length > 1 ? 's' : ''} sent!`, 'success');
      } else if (!activeCampaignId) {
        // Brand new draft with no invitees yet — persist it as a campaign so
        // the message/cover/name aren't lost, and lock in the campaign id.
        const res = await invitationApi.createInvitations({
          event_id: Number(selectedEventId),
          invitees: [],
          ...draftPayload,
        });
        if (res.campaign_id) {
          refreshCampaignId = res.campaign_id;
          setActiveCampaignId(res.campaign_id);
          navigate(`/invitations/create?campaign_id=${res.campaign_id}`, { replace: true });
        }
        toast('Draft saved.', 'success');
      } else {
        toast('Changes saved.', 'success');
      }

      setCoverFile(null);
      await loadSaved(refreshCampaignId || '');
    } catch (e) {
      toast(e.response?.data?.error || e.message || 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteInvitation(invitationId) {
    try {
      await invitationApi.deleteInvitation(invitationId);
      toast('Invitation revoked.', 'success');
      await loadSaved(activeCampaignId);
    } catch (e) {
      toast(e.response?.data?.error || 'Failed to revoke invitation', 'error');
    }
  }

  async function handleNudge(invitationId) {
    try {
      await invitationApi.nudge(invitationId);
      toast('Nudge sent!', 'success');
      loadSaved(activeCampaignId);
    } catch (e) {
      toast(e.response?.data?.error || 'Could not send nudge', 'error');
    }
  }

  // ---------------------------------------------------------------------------
  // Sub-panes
  // ---------------------------------------------------------------------------

  const leftPane = (
    <Stack spacing={2.5}>
      {/* Stats */}
      {(savedInvitations.length > 0 || stats.total > 0) && (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5 }}>
          <Grid container spacing={1} textAlign="center">
            {[
              { label: 'Invited', value: stats.total, color: 'text.primary' },
              { label: 'Attending', value: stats.accepted, color: 'success.main' },
              { label: 'Declined', value: stats.declined, color: 'error.main' },
              { label: 'Pending', value: stats.pending, color: 'warning.main' },
            ].map(s => (
              <Grid size={{ xs: 3 }} key={s.label}>
                <Typography variant="h6" fontWeight={700} color={s.color}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Invitation name (identifies this campaign) */}
      <TextField
        label="Invitation Name *"
        placeholder="e.g. Family, Office colleagues, Wedding guests…"
        value={campaignName}
        onChange={e => setCampaignName(e.target.value)}
        fullWidth
        size="small"
        inputProps={{ maxLength: 100 }}
      />

      {/* Event: locked when managing, selectable on fresh create */}
      {isEventLocked ? (
        <Stack spacing={1}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5 }}>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.25}>Event</Typography>
            <Typography variant="body2" fontWeight={700}>{selectedEvent?.event_name}</Typography>
            {selectedEvent?.event_date && (
              <Typography variant="caption" color="text.secondary" display="block">
                {new Date(selectedEvent.event_date).toLocaleDateString()}
              </Typography>
            )}
            {selectedEvent?.event_type && (
              <Chip label={selectedEvent.event_type} size="small" variant="outlined" sx={{ mt: 0.5 }} />
            )}
          </Paper>
          {selectedEvent && (
            <Button variant="outlined" startIcon={<OpenInNewIcon />} size="small"
              onClick={() => navigate(`/event/${selectedEvent.event_id}`)}
              sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
              View Event Page
            </Button>
          )}
        </Stack>
      ) : (
        <Stack spacing={1}>
          <TextField
            select
            label="Select Event *"
            value={selectedEventId}
            onChange={e => setSelectedEventId(e.target.value)}
            fullWidth
            size="small"
          >
            {events.length === 0 && <MenuItem value="">No events found</MenuItem>}
            {events.map(ev => (
              <MenuItem key={ev.event_id} value={String(ev.event_id)}>{ev.event_name}</MenuItem>
            ))}
          </TextField>
          {selectedEvent && (
            <Button variant="outlined" startIcon={<OpenInNewIcon />} size="small"
              onClick={() => navigate(`/event/${selectedEvent.event_id}`)}
              sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
              View Event Page
            </Button>
          )}
        </Stack>
      )}

      {/* Cover image */}
      <Box>
        <Typography variant="body2" fontWeight={600} mb={0.5}>Cover Image (optional)</Typography>
        <input
          ref={coverInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleCoverChange}
        />
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="outlined"
            startIcon={<ImageIcon />}
            onClick={() => coverInputRef.current?.click()}
            sx={{ textTransform: 'none' }}
          >
            {coverFile ? 'Change image' : 'Add image'}
          </Button>
          {coverFile && (
            <Button size="small" color="error" onClick={() => {
              setCoverFile(null);
              if (coverPreview) { URL.revokeObjectURL(coverPreview); setCoverPreview(''); }
            }} sx={{ textTransform: 'none' }}>
              Remove
            </Button>
          )}
        </Stack>
        {coverPreview && (
          <Box
            component="img"
            src={coverPreview}
            alt="Cover preview"
            sx={{ mt: 1.5, width: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 2 }}
          />
        )}
      </Box>

      {/* Message with preview toggle */}
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
          <Typography variant="body2" fontWeight={600}>Invitation Message</Typography>
          <Stack direction="row" spacing={0.5}>
            <Button
              size="small"
              variant={!markdownPreview ? 'contained' : 'outlined'}
              onClick={() => setMarkdownPreview(false)}
              sx={{ textTransform: 'none', fontSize: 12, minWidth: 0, px: 1.5 }}
            >Edit</Button>
            <Button
              size="small"
              variant={markdownPreview ? 'contained' : 'outlined'}
              onClick={() => setMarkdownPreview(true)}
              sx={{ textTransform: 'none', fontSize: 12, minWidth: 0, px: 1.5 }}
            >Preview</Button>
          </Stack>
        </Stack>
        {markdownPreview ? (
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5, minHeight: 120 }}>
            {message.trim() ? (
              <MarkdownContent markdown={message} />
            ) : (
              <Typography variant="body2" color="text.disabled" fontStyle="italic">
                Nothing to preview yet.
              </Typography>
            )}
          </Paper>
        ) : (
          <TextField
            multiline
            rows={5}
            value={message}
            onChange={e => setMessage(e.target.value)}
            fullWidth
            placeholder={"You're warmly invited to join us...\n\n**Bold**, *italic*, [links](https://...) supported."}
            inputProps={{ maxLength: 4000 }}
            helperText={`${message.length}/4000 — supports Markdown`}
          />
        )}
      </Box>

      {!isMobile && (
        <Button
          variant="contained"
          onClick={handleSendInvitations}
          disabled={saving || !selectedEventId}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : invitees.length > 0 ? <SendIcon /> : <SaveIcon />}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {saving ? 'Saving…' : saveButtonLabel}
        </Button>
      )}
    </Stack>
  );

  const rightPane = (
    <Stack spacing={2}>
      {/* Network search */}
      <Box>
        <Typography variant="body2" fontWeight={600} mb={0.5}>Add from your network</Typography>
        <Box sx={{ position: 'relative' }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search connections…"
            value={networkSearch}
            onChange={e => setNetworkSearch(e.target.value)}
            InputProps={{
              endAdornment: networkLoading ? <CircularProgress size={16} /> : null,
            }}
          />
          {networkResults.length > 0 && (
            <Paper
              elevation={4}
              sx={{
                position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0,
                maxHeight: 220, overflowY: 'auto', borderRadius: 1.5, mt: 0.5,
              }}
            >
              {networkResults.map(person => {
                const added = isAlreadyAdded(person) || isInQueue(person);
                return (
                  <Stack
                    key={person.customer_id}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    px={1.5}
                    py={1}
                    sx={{ cursor: added ? 'default' : 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() => !added && addToQueue(person)}
                  >
                    <Avatar src={person.profile_picture || undefined} sx={{ width: 30, height: 30, fontSize: 13 }}>
                      {avatarLetters(person.customer_name)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap>{person.customer_name}</Typography>
                      {person.username && (
                        <Typography variant="caption" color="text.secondary" noWrap>@{person.username}</Typography>
                      )}
                    </Box>
                    {added && <Chip label="Added" size="small" color="success" variant="outlined" />}
                  </Stack>
                );
              })}
            </Paper>
          )}
        </Box>
      </Box>

      <Divider />

      {/* Invite list header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body2" fontWeight={600}>
          Invite list ({invitees.length + savedInvitations.length})
        </Typography>
        <Button
          size="small"
          startIcon={<PersonAddAlt1Icon />}
          onClick={() => setShowAddForm(v => !v)}
          sx={{ textTransform: 'none', fontSize: 12 }}
        >
          Add contact
        </Button>
      </Stack>

      {/* Inline add form */}
      <Fade in={showAddForm}>
        <Box sx={{ display: showAddForm ? 'block' : 'none' }}>
          <AddContactRow
            onSave={(contact) => {
              addToQueue(contact);
              setShowAddForm(false);
            }}
            onCancel={() => setShowAddForm(false)}
          />
        </Box>
      </Fade>

      {/* Pending (not yet sent) invitees */}
      {invitees.length > 0 && (
        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
            Queued to send ({invitees.length})
          </Typography>
          {invitees.map((inv, idx) => (
            <InviteeRow
              key={idx}
              invitee={inv}
              savedInv={null}
              onRemove={removeFromQueue}
              onNudge={handleNudge}
              onChat={setChatInv}
            />
          ))}
        </Stack>
      )}

      {/* Already sent invitations */}
      {savedInvitations.length > 0 && (
        <Stack spacing={1}>
          {invitees.length > 0 && (
            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
              Already invited ({savedInvitations.length})
            </Typography>
          )}
          {savedInvitations.map(inv => {
            const person = {
              customer_id: inv.recipient_id,
              customer_name: inv.recipient_cm_name || inv.recipient_name || inv.recipient_email || 'Contact',
              email: inv.recipient_email,
              profile_picture: inv.recipient_profile_picture,
            };
            return (
              <InviteeRow
                key={inv.invitation_id}
                invitee={person}
                savedInv={inv}
                onRemove={() => {}}
                onRevoke={handleDeleteInvitation}
                onNudge={handleNudge}
                onChat={setChatInv}
              />
            );
          })}
        </Stack>
      )}

      {invitees.length === 0 && savedInvitations.length === 0 && !showAddForm && (
        <Box sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
          <PersonAddAlt1Icon sx={{ fontSize: 36, opacity: 0.3, mb: 0.5 }} />
          <Typography variant="body2">Search your network or add a contact to start.</Typography>
        </Box>
      )}

      {isMobile && (
        <Button
          variant="contained"
          onClick={handleSendInvitations}
          disabled={saving || !selectedEventId}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : invitees.length > 0 ? <SendIcon /> : <SaveIcon />}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {saving ? 'Saving…' : saveButtonLabel}
        </Button>
      )}
    </Stack>
  );

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.default', pb: isMobile ? 8 : 0 }}>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Snackbar
          open={snack.open}
          autoHideDuration={4000}
          onClose={() => setSnack(s => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity={snack.sev} variant="filled">{snack.msg}</Alert>
        </Snackbar>

        <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/invitations')}
            sx={{ textTransform: 'none' }}>
            Back
          </Button>
          <Typography variant="h5" fontWeight={700}>
            {activeCampaignId ? 'Manage Invitation' : 'Create Invitation'}
          </Typography>
        </Stack>

        {pageLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
            <CircularProgress />
          </Box>
        ) : isMobile ? (
          <Box>
            {mobileStep === 0 ? (
              <>
                {leftPane}
                <Button
                  fullWidth
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ mt: 3, textTransform: 'none', fontWeight: 600 }}
                  onClick={() => {
                    if (!selectedEventId) { toast('Please select an event first.', 'error'); return; }
                    setMobileStep(1);
                  }}
                >
                  Next: Add Invites
                </Button>
              </>
            ) : (
              <>
                <Button startIcon={<ArrowBackIcon />} onClick={() => setMobileStep(0)}
                  sx={{ mb: 2, textTransform: 'none' }}>
                  Back to Draft
                </Button>
                {rightPane}
              </>
            )}
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid size={{ md: 5 }}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                {leftPane}
              </Paper>
            </Grid>
            <Grid size={{ md: 7 }}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, maxHeight: '80vh', overflowY: 'auto' }}>
                {rightPane}
              </Paper>
            </Grid>
          </Grid>
        )}
      </Container>

      {/* Chat popup */}
      {chatInv && (
        <InvitationChat
          invitation={chatInv}
          viewerRole="organiser"
          onClose={() => {
            setChatInv(null);
            loadSaved(activeCampaignId);
          }}
        />
      )}
    </Box>
  );
}
